import { Loader2, Plus } from 'lucide-react'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './../../ui/select.tsx'
import { Button } from './../../ui/button.tsx'
import { Label } from './../../ui/label.tsx'
import { Input } from './../../ui/input.tsx'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from './../../ui/dialog.tsx'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useState } from 'react';
import { usePartnerBalance } from '../../../hooks/usePartnerBalance.ts';
import { usePartnerSubAccountCreation } from '../../../hooks/usePartnerSubAccountCreation.ts';
import { useCreateSubAccount } from '../../../hooks/useSubAccounts.ts';
import { toast } from 'sonner';
import { Success } from './Success.tsx';
import { generateRandomPassword } from '@/utils/partnerUtils.ts'
// Form validation schema
const createUserSchema = z.object({
  email: z.string().email('Invalid email address'),
  name: z.string().min(2, 'Name must be at least 2 characters'),
  plan: z.string().min(1, 'Please select a plan'),
  password: z.string().min(6, 'Password must be at least 6 characters'),
  credits: z.number().min(0, 'Credits cannot be negative').optional(),
})

type CreateUserFormData = z.infer<typeof createUserSchema>

export const CreateSubAccount = () => {
  const [open, setOpen] = useState(false);
  const [success, setSuccess] = useState(false);
  const [createdAccount, setCreatedAccount] = useState<{email: string, password: string, name: string} | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);

  const createSubAccountMutation = useCreateSubAccount();

  // Function to generate random password

  const { data: partnerBalance } = usePartnerBalance();
  const { availablePlans, canCreateSubAccounts, isLoading: plansLoading } = usePartnerSubAccountCreation();

  const { register, handleSubmit, formState: { errors, isSubmitting }, reset, setValue, watch } = useForm<CreateUserFormData>({
    resolver: zodResolver(createUserSchema),
    defaultValues: { email: '', name: '', plan: '', password: '', credits: 0 }
  })
  const watchedCredits = watch('credits', 0)

  const onSubmit = async (data: CreateUserFormData) => {
    // Clear previous email error
    setEmailError(null);
    
    try {
      // Validate free credits
      if (data.credits && data.credits > 0 && (!partnerBalance || data.credits > partnerBalance.current_balance)) {
        return toast.error('Insufficient credits available for free credits allocation');
      }

      await createSubAccountMutation.mutateAsync({
        email: data.email,
        full_name: data.name,
        plan_identifier: data.plan,
        password: data.password,
        free_credits: data.credits && data.credits > 0 ? data.credits : undefined,
      });
      const isCredits = data.credits && data.credits > 0 ;
      toast.success(`Sub-account created for ${data.email} ${isCredits !==0 && `with ${data?.credits} free credits`}. Share the password securely with the user.`);
      setCreatedAccount({email: data.email, password: data.password, name: data.name });
      setSuccess(true);
      reset();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      
      // Check if it's an email already exists error
      if (errorMessage.toLowerCase().includes('email') && 
          (errorMessage.toLowerCase().includes('already') || 
           errorMessage.toLowerCase().includes('exists') ||
           errorMessage.toLowerCase().includes('registered'))) {
        setEmailError('An account with this email already exists');
        // Don't close modal, just show error under email field
      } else {
        // For other errors, show toast and close modal
        toast.error(`Failed to create account: ${errorMessage}`);
        setOpen(false);
      }
    }
  }

  if(!canCreateSubAccounts) return null;

  return (
    <Dialog open={open} onOpenChange={(open) => {
      setOpen(open);
      if(!open) {
        setSuccess(false);
        setCreatedAccount(null);
        setEmailError(null); // Clear email error when closing modal
      }
    }} >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Create Sub-Account</DialogTitle>
          <DialogDescription>
            Add a new sub-account to your partner dashboard
          </DialogDescription>
        </DialogHeader>

        {success ? (
          <Success  
            email={createdAccount?.email || ''}
            password={createdAccount?.password || ''}
            name={createdAccount?.name || ''}
            onClose={() => {
              setSuccess(false);
              setOpen(false);
            }}
          />
        ) : (
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 py-4">
          <div>
            <Label htmlFor="email">Customer Email *</Label>
            <Input 
              id="email"
              {...register('email')}
              placeholder="email@domain.com"
              type="email"
              className={errors.email || emailError ? 'border-red-500' : ''}
              onChange={(e) => {
                if (emailError) setEmailError(null);
                register('email').onChange(e);
              }}
            />
            {errors.email && (
              <p className="text-sm text-red-500 mt-1">{errors.email.message}</p>
            )}
            {emailError && (
              <p className="text-sm text-red-500 mt-1">{emailError}</p>
            )}
          </div>
          
          <div>
            <Label htmlFor="name">Customer Name *</Label>
            <Input 
              id="name"
              {...register('name')}
              placeholder="John Doe"
              className={errors.name ? 'border-red-500' : ''}
            />
            {errors.name && (
              <p className="text-sm text-red-500 mt-1">{errors.name.message}</p>
            )}
          </div>

          <div>
            <Label htmlFor="password">Password *</Label>
            <div className="flex space-x-2 relative">
              <Input 
                id="password"
                {...register('password')}
                placeholder="Create password for user"
                type="text"
                className={errors.password ? 'border-red-500' : ''}
              />
              <Button
                type="button"
                size="sm"
                className="absolute right-2 top-1/2 -translate-y-1/2 p-0 h-6 px-2 text-xs"
                variant="outline"
                onClick={() => setValue('password', generateRandomPassword())}
                title="Generate random password"
              >
                Random
              </Button>
            </div>
            {errors.password && (
              <p className="text-sm text-red-500 mt-1">{errors.password.message}</p>
            )}
            <p className="text-xs text-muted-foreground mt-1">
              Share this password securely with the user to login
            </p>
          </div>
          
          <div>
            <Label htmlFor="plan">Starting Plan *</Label>
            <Select onValueChange={(value: string) => setValue('plan', value)}>
              <SelectTrigger className={errors.plan ? 'border-red-500' : ''}>
                <SelectValue placeholder="Select a plan" />
              </SelectTrigger>
              <SelectContent>
                {availablePlans?.map((plan) => (
                  <SelectItem key={plan.id} value={plan.identifier}>
                    {plan.name} - ${plan.price}/{plan.billing_period || 'mo'}
                  </SelectItem>
                ))}
                {(!availablePlans || availablePlans.length === 0) && (
                  <SelectItem value="" disabled>
                    {plansLoading ? 'Loading plans...' : 'No plans available'}
                  </SelectItem>
                )}
              </SelectContent>
            </Select>
            {errors.plan && (
              <p className="text-sm text-red-500 mt-1">{errors.plan.message}</p>
            )}
          </div>
          
          {partnerBalance?.current_balance && partnerBalance?.current_balance > 0 && <div>
            <Label htmlFor="credits">Free Credits (Optional)</Label>
            <div className="space-y-2">
              <Input 
                id="credits"
                {...register('credits', { valueAsNumber: true })}
                placeholder="0"
                type="number"
                min="0"
                max={partnerBalance?.current_balance || 0}
                className={errors.credits ? 'border-red-500' : ''}
              />
              {errors.credits && (
                <p className="text-sm text-red-500">{errors.credits.message}</p>
              )}
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>Your available credits: {partnerBalance?.current_balance?.toLocaleString() || 0}</span>
                <span>Max: {partnerBalance?.current_balance?.toLocaleString() || 0}</span>
              </div>
              {watchedCredits && watchedCredits > 0 && (
                <p className="text-xs text-blue-600">
                  User will receive {watchedCredits.toLocaleString()} credits upon account creation
                </p>
              )}
            </div>
          </div>}
          
          <div className="flex justify-end space-x-2 pt-4">
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => {
                reset()
                setOpen(false)
              }}
              
            >
              Cancel
            </Button>
            <Button 
              type="submit"
              disabled={isSubmitting || createSubAccountMutation.isPending}
            >
              {isSubmitting || createSubAccountMutation.isPending ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Creating...</>
              ) : (
                'Create Account'
              )}
            </Button>
          </div>
        </form>
        )}

      </DialogContent>
      <DialogTrigger asChild>
        <Button>
          <Plus className="size-4 mr-2" />
          Create New
        </Button>
      </DialogTrigger>
    </Dialog>
  )
}